import React from 'react';
import axios from 'axios';
import { useDashboard } from '../contexts/DashboardContext';
import moment from 'moment';
import CouponAnalytics from '../components/CouponAnalytics';
import EmptyState from '../components/common/EmptyState';
import StatusBadge from '../components/common/StatusBadge';
import Badge from '../components/common/Badge';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const DashboardHome = () => {
  const {
    activeTab, appointments, clients, dashboardQuotes, dashboardSections, draggedItem, draggedSection,
    editAppointment, editClient, editTask, fetchAppointmentMedia, formatTableDate, formatTableTime, getTodaysAppointments,
    quoteActionLoading, quoteFilterTab, selectedDashboardQuote,
    serviceTypes, setActiveTab, setDashboardSections, setDraggedSection, setPropertyClients,
    setQuoteFilterTab, setSelectedDashboardQuote, setSelectedListingForEdit,
    setShowAppointmentDetails, setShowDashboardCustomize, setTempDashboardSections,
    setTodaysAppointmentsView, showDashboardCustomize, showToast, stats, tasksSummary,
    tempDashboardSections, todaysAppointmentsView,
  } = useDashboard();

  return (
    <div className="space-y-12 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" >Dashboard</h2>
        <button
          onClick={() => {
            setTempDashboardSections([...dashboardSections]);
            setShowDashboardCustomize(true);
          }}
          className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          data-testid="customize-dashboard-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          Customize
        </button>
      </div>

      {/* Render dashboard sections based on order and visibility */}
      {(() => {
        const visibleSections = dashboardSections.filter(s => s.visible);
        const recentApptsVisible = visibleSections.some(s => s.id === 'recent-appointments');
        const recentClientsVisible = visibleSections.some(s => s.id === 'recent-clients');
        const bothRecentVisible = recentApptsVisible && recentClientsVisible;
        
        // Get the first recent section's position for combined rendering
        const firstRecentIndex = visibleSections.findIndex(s => s.id === 'recent-appointments' || s.id === 'recent-clients');
        
        return visibleSections.map((section, index) => {
          // Skip the second recent section if both are visible (it's combined with the first)
          if (bothRecentVisible && 
              (section.id === 'recent-appointments' || section.id === 'recent-clients') && 
              index !== firstRecentIndex) {
            return null;
          }
          
          return (
            <div key={section.id}>
          {section.id === 'todays-appointments' && (
            /* Today's Appointments */
            <div>
              <div className="mb-4 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Today's Appointments
                </h3>
                {getTodaysAppointments().length > 0 && (
                  <div className="flex items-center bg-gray-200 dark:bg-gray-600 rounded-lg p-1">
                    <button
                      onClick={() => setTodaysAppointmentsView('list')}
                      className={`px-3 py-1 rounded text-sm transition-all ${
                        todaysAppointmentsView === 'list' 
                          ? 'bg-white text-gray-700 font-medium' 
                          : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
                      }`}
                    >
                      List
                    </button>
                    <button
                      onClick={() => setTodaysAppointmentsView('map')}
                      className={`px-3 py-1 rounded text-sm transition-all ${
                        todaysAppointmentsView === 'map' 
                          ? 'bg-white text-gray-700 font-medium' 
                          : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
                      }`}
                    >
                      Map
                    </button>
                  </div>
                )}
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
              {getTodaysAppointments().length === 0 ? (
                <EmptyState
                  icon="📅"
                  title="No appointments today"
                  description="Your schedule is clear for today"
                />
              ) : todaysAppointmentsView === 'list' ? (
                <div className="overflow-x-auto">
                  <table className="clara-table min-w-full">
                    <thead>
                      <tr>
                        <th className="text-left px-4 py-2">Client</th>
                        <th className="text-left px-4 py-2">Property</th>
                        <th className="text-left px-4 py-2">Time</th>
                        <th className="text-left px-4 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getTodaysAppointments()
                        .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time))
                        .map(appointment => (
                        <tr 
                          key={appointment.id} 
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedAppointmentDetails(appointment);
                            setShowAppointmentDetails(true);
                          }}
                        >
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-900">{appointment.client_name}</div>
                            {!appointment.isGoogleEvent && (
                              <div className="text-xs text-gray-500">
                                {serviceTypes.find(s => s.value === appointment.service_type)?.label || appointment.service_type}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {!appointment.isGoogleEvent && (
                              <div className="text-sm text-gray-900">{appointment.property_address}</div>
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatTableTime(appointment.scheduled_time)}</div>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <StatusBadge status={appointment.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="h-[600px] relative">
                  <div id="todays-appointments-map" className="w-full h-full"></div>
                  {getTodaysAppointments().filter(a => a.property_address && !a.isGoogleEvent).length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                      <div className="text-center py-8 text-gray-500">
                        <p>No appointments with addresses to display on map</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>
          )}
          
          {section.id === 'tasks-overview' && (
            /* Task Summary Widget */
            <div>
              <div className="mb-4 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Tasks Overview
                </h3>
                <button
                  onClick={() => setActiveTab('tasks')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View All Tasks →
                </button>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Overdue Tasks */}
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      Overdue ({tasksSummary.overdue?.length || 0})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {tasksSummary.overdue?.slice(0, 5).map(task => {
                        const appointment = appointments.find(apt => apt.id === task.appointment_id);
                        const dueDate = new Date(task.due_date);
                        return (
                          <div 
                            key={task.id} 
                            onClick={() => {
                              editTask(task);
                              setActiveTab('tasks');
                            }}
                            className="bg-white dark:bg-gray-800 p-3 rounded border border-red-200 dark:border-red-800 cursor-pointer hover:shadow-sm transition-shadow flex items-center gap-3"
                          >
                            {/* Due Date Column */}
                            <div className="flex-shrink-0 w-12 text-center bg-red-100 dark:bg-red-900/40 rounded-lg py-1">
                              <p className="text-xs text-red-600 dark:text-red-400 uppercase">{dueDate.toLocaleDateString('en-US', { month: 'short' })}</p>
                              <p className="text-xl font-bold text-red-700 dark:text-red-300">{dueDate.getDate()}</p>
                            </div>
                            {/* Task Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{task.title}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{appointment?.client_name || 'No client'}</p>
                            </div>
                          </div>
                        );
                      })}
                      {(!tasksSummary.overdue || tasksSummary.overdue.length === 0) && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No overdue tasks</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Due Today */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      Due Today ({tasksSummary.due_today?.length || 0})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {tasksSummary.due_today?.slice(0, 5).map(task => {
                        const appointment = appointments.find(apt => apt.id === task.appointment_id);
                        const dueDate = new Date(task.due_date);
                        return (
                          <div 
                            key={task.id}
                            onClick={() => {
                              editTask(task);
                              setActiveTab('tasks');
                            }}
                            className="bg-white dark:bg-gray-800 p-3 rounded border border-amber-200 dark:border-amber-800 cursor-pointer hover:shadow-sm transition-shadow flex items-center gap-3"
                          >
                            {/* Due Date Column */}
                            <div className="flex-shrink-0 w-12 text-center bg-amber-100 dark:bg-amber-900/40 rounded-lg py-1">
                              <p className="text-xs text-amber-600 dark:text-amber-400 uppercase">{dueDate.toLocaleDateString('en-US', { month: 'short' })}</p>
                              <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{dueDate.getDate()}</p>
                            </div>
                            {/* Task Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{task.title}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{appointment?.client_name || 'No client'}</p>
                            </div>
                          </div>
                        );
                      })}
                      {(!tasksSummary.due_today || tasksSummary.due_today.length === 0) && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No tasks due today</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Upcoming */}
                  <div className="bg-sky-50 dark:bg-sky-900/20 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">📅 Upcoming Tasks ({tasksSummary.upcoming?.length || 0})</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto mt-3">
                      {tasksSummary.upcoming?.slice(0, 5).map(task => {
                        const appointment = appointments.find(apt => apt.id === task.appointment_id);
                        const dueDate = new Date(task.due_date);
                        return (
                          <div 
                            key={task.id}
                            onClick={() => {
                              editTask(task);
                              setActiveTab('tasks');
                            }}
                            className="bg-white dark:bg-gray-800 p-3 rounded border border-sky-200 dark:border-sky-800 cursor-pointer hover:shadow-sm transition-shadow flex items-center gap-3"
                          >
                            {/* Due Date Column */}
                            <div className="flex-shrink-0 w-12 text-center bg-sky-100 dark:bg-sky-900/40 rounded-lg py-1">
                              <p className="text-xs text-sky-600 dark:text-sky-400 uppercase">{dueDate.toLocaleDateString('en-US', { month: 'short' })}</p>
                              <p className="text-xl font-bold text-sky-700 dark:text-sky-300">{dueDate.getDate()}</p>
                            </div>
                            {/* Task Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{task.title}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{appointment?.client_name || 'No client'}</p>
                            </div>
                          </div>
                        );
                      })}
                      {(!tasksSummary.upcoming || tasksSummary.upcoming.length === 0) && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No upcoming tasks</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {tasksSummary.overdue?.length === 0 && tasksSummary.due_today?.length === 0 && tasksSummary.upcoming?.length === 0 && (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">✅</div>
                    <p className="text-gray-500">All caught up! No pending tasks.</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {section.id === 'quotes-tracker' && (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Appointment Quotes
                </h3>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
                {/* Filter tabs */}
                <div className="flex border-b border-gray-100 dark:border-gray-700 overflow-x-auto">
                  {[
                    { key: 'all', label: 'All', count: dashboardQuotes.length, color: 'text-gray-700', activeColor: 'border-gray-900 text-gray-900', activeBg: '' },
                    { key: 'pending', label: 'Pending', count: dashboardQuotes.filter(q => q.status === 'pending' || q.status === 'created').length, color: 'text-amber-600', activeColor: 'border-amber-500 text-amber-700', activeBg: 'bg-amber-50' },
                    { key: 'sent', label: 'Sent', count: dashboardQuotes.filter(q => q.status === 'sent').length, color: 'text-blue-600', activeColor: 'border-blue-500 text-blue-700', activeBg: 'bg-blue-50' },
                    { key: 'accepted', label: 'Accepted', count: dashboardQuotes.filter(q => q.status === 'accepted').length, color: 'text-green-600', activeColor: 'border-green-500 text-green-700', activeBg: 'bg-green-50' },
                    { key: 'expired', label: 'Expired', count: dashboardQuotes.filter(q => q.status === 'expired').length, color: 'text-gray-400', activeColor: 'border-gray-400 text-gray-600', activeBg: 'bg-gray-50' },
                    { key: 'cancelled', label: 'Cancelled', count: dashboardQuotes.filter(q => q.status === 'cancelled').length, color: 'text-red-500', activeColor: 'border-red-500 text-red-700', activeBg: 'bg-red-50' }
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setQuoteFilterTab(tab.key)}
                      className={`flex-1 min-w-0 px-3 py-3 text-center border-b-2 transition-colors ${
                        quoteFilterTab === tab.key
                          ? `${tab.activeColor} font-semibold`
                          : `border-transparent ${tab.color} hover:bg-gray-50 dark:hover:bg-gray-700`
                      }`}
                      data-testid={`quote-tab-${tab.key}`}
                    >
                      <span className="text-lg font-bold block">{tab.count}</span>
                      <span className="text-xs">{tab.label}</span>
                    </button>
                  ))}
                </div>
                {/* Filtered quotes list */}
                <div className="p-4">
                  {(() => {
                    const filtered = quoteFilterTab === 'all'
                      ? dashboardQuotes
                      : quoteFilterTab === 'pending'
                        ? dashboardQuotes.filter(q => q.status === 'pending' || q.status === 'created')
                        : dashboardQuotes.filter(q => q.status === quoteFilterTab);
                    
                    if (filtered.length === 0) {
                      return <p className="text-sm text-center py-4 text-gray-500">
                        {dashboardQuotes.length === 0 ? 'No quotes yet. Use the Quote Builder when scheduling appointments.' : `No ${quoteFilterTab} quotes.`}
                      </p>;
                    }
                    return (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {filtered.slice(0, 20).map(quote => {
                        const isActive = ['pending', 'created', 'sent'].includes(quote.status);
                        const expiresAt = quote.expires_at ? new Date(quote.expires_at) : null;
                        const hoursLeft = expiresAt ? Math.max(0, Math.round((expiresAt - new Date()) / 3600000)) : null;
                        return (
                          <div
                            key={quote.id}
                            onClick={() => setSelectedDashboardQuote(quote)}
                            className="flex items-center justify-between py-3 px-4 rounded-lg cursor-pointer transition-colors bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                            data-testid={`quote-row-${quote.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{quote.client_name}</p>
                              <p className="text-xs text-gray-500 truncate">{quote.property_address}</p>
                            </div>
                            <div className="flex items-center gap-3 ml-3">
                              {(() => {
                                const dateOpts = { month: 'short', day: 'numeric' };
                                if (quote.status === 'accepted' && quote.accepted_at)
                                  return <span className="text-xs text-green-600 whitespace-nowrap">Accepted {new Date(quote.accepted_at).toLocaleDateString('en-US', dateOpts)}</span>;
                                if (quote.status === 'cancelled' && quote.cancelled_at)
                                  return <span className="text-xs text-red-500 whitespace-nowrap">Cancelled {new Date(quote.cancelled_at).toLocaleDateString('en-US', dateOpts)}</span>;
                                if (quote.status === 'expired' && quote.expires_at)
                                  return <span className="text-xs text-gray-400 whitespace-nowrap">Expired {new Date(quote.expires_at).toLocaleDateString('en-US', dateOpts)}</span>;
                                if (quote.sent_at)
                                  return <span className="text-xs text-gray-400 whitespace-nowrap">Sent on {new Date(quote.sent_at).toLocaleDateString('en-US', dateOpts)}</span>;
                                return null;
                              })()}
                              {hoursLeft !== null && isActive && (
                                <span className={`text-xs ${hoursLeft <= 6 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                  {hoursLeft}h left
                                </span>
                              )}
                              <div className="text-right">
                                <p className="text-xs text-gray-500">{quote.time_slots?.length || 0} option{(quote.time_slots?.length || 0) !== 1 ? 's' : ''}</p>
                                {quote.total_price > 0 && <p className="text-xs font-medium text-gray-700 dark:text-gray-300">${quote.total_price?.toLocaleString('en-US', {minimumFractionDigits: 2})}</p>}
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                quote.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                                quote.status === 'expired' ? 'bg-gray-100 text-gray-500' :
                                quote.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-amber-100 text-amber-800'
                              }`}>
                                {quote.status === 'created' ? 'Pending' : quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    );
                  })()}
                </div>
              </div>

              {/* Quote Detail Popup */}
              {selectedDashboardQuote && (() => {
                const quote = selectedDashboardQuote;
                const isActive = ['pending', 'created', 'sent'].includes(quote.status);
                const expiresAt = quote.expires_at ? new Date(quote.expires_at) : null;
                const hoursLeft = expiresAt ? Math.max(0, Math.round((expiresAt - new Date()) / 3600000)) : null;
                return (
                  <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="quote-detail-modal">
                    <div className="fixed inset-0 bg-black/60" onClick={() => setSelectedDashboardQuote(null)} />
                    <div className="relative z-10 bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
                      {/* Header */}
                      <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{quote.client_name}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">{quote.property_address}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                            quote.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            quote.status === 'expired' ? 'bg-gray-100 text-gray-500' :
                            quote.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {quote.status === 'created' ? 'Pending' : quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1)}
                          </span>
                          <button onClick={() => setSelectedDashboardQuote(null)} className="text-gray-400 hover:text-gray-600 p-1">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-5 space-y-4">
                        {/* Client info */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Client Email</p>
                            <p className="text-sm text-gray-900 dark:text-gray-200">{quote.client_email || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Property Size</p>
                            <p className="text-sm text-gray-900 dark:text-gray-200">{quote.property_sqft ? `${quote.property_sqft} sq ft` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Total Price</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-200">{quote.total_price > 0 ? `$${quote.total_price.toLocaleString('en-US', {minimumFractionDigits: 2})}` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 mb-1">
                              {quote.status === 'accepted' ? 'Accepted Date' : quote.status === 'cancelled' ? 'Cancelled Date' : quote.status === 'expired' ? 'Expired Date' : 'Date Sent'}
                            </p>
                            <p className="text-sm text-gray-900 dark:text-gray-200">{(() => {
                              const fmt = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
                              if (quote.status === 'accepted' && quote.accepted_at) return new Date(quote.accepted_at).toLocaleDateString('en-US', fmt);
                              if (quote.status === 'cancelled' && quote.cancelled_at) return new Date(quote.cancelled_at).toLocaleDateString('en-US', fmt);
                              if (quote.status === 'expired' && quote.expires_at) return new Date(quote.expires_at).toLocaleDateString('en-US', fmt);
                              if (quote.sent_at) return new Date(quote.sent_at).toLocaleDateString('en-US', fmt);
                              return 'Not sent yet';
                            })()}</p>
                          </div>
                          {hoursLeft !== null && isActive && (
                            <div>
                              <p className="text-xs text-gray-400 mb-1">Expires In</p>
                              <p className={`text-sm font-medium ${hoursLeft <= 6 ? 'text-red-600' : 'text-gray-900 dark:text-gray-200'}`}>{hoursLeft} hours</p>
                            </div>
                          )}
                        </div>

                        {/* Time slots */}
                        {quote.time_slots?.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-400 font-medium mb-2">Time Options</p>
                            <div className="space-y-2">
                              {quote.time_slots.map((slot, si) => (
                                <div key={si} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg">
                                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{si + 1}</div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{slot.date_display || slot.date}</p>
                                    <p className="text-xs text-gray-500">{slot.time_12h || slot.time} &middot; {slot.photographer_name}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Services */}
                        {quote.product_pricing?.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-400 font-medium mb-2">Services</p>
                            <div className="space-y-1">
                              {quote.product_pricing.map((p, pi) => (
                                <div key={pi} className="flex justify-between text-sm px-1">
                                  <span className="text-gray-700 dark:text-gray-300">{p.product_name}</span>
                                  {p.price > 0 && <span className="text-gray-900 dark:text-white font-medium">${p.price.toFixed(2)}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {quote.status === 'accepted' && (
                          <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            <span className="text-sm font-medium">Client accepted this quote</span>
                          </div>
                        )}
                      </div>

                      {/* Footer with actions */}
                      {isActive && (
                        <div className="flex gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => handleQuoteReminder(quote.id)}
                            disabled={quoteActionLoading === quote.id}
                            className="flex-1 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                            data-testid={`quote-remind-${quote.id}`}
                          >
                            {quoteActionLoading === quote.id ? (
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            )}
                            Send Reminder
                          </button>
                          <button
                            onClick={() => { handleQuoteCancel(quote.id); }}
                            disabled={quoteActionLoading === quote.id}
                            className="px-4 py-2.5 text-sm font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            data-testid={`quote-cancel-${quote.id}`}
                          >
                            Cancel Quote
                          </button>
                        </div>
                      )}
                      <div className="flex justify-center p-5 pt-0">
                        <button
                          onClick={() => handleQuoteDelete(quote.id)}
                          disabled={quoteActionLoading === quote.id}
                          className="px-3 py-1.5 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          data-testid={`quote-delete-${quote.id}`}
                        >
                          Delete Quote
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {section.id === 'coupon-analytics' && (
            <CouponAnalytics />
          )}

          {(section.id === 'recent-appointments' || section.id === 'recent-clients') && (
            /* Recent Appointments & Recent Clients - Side by side if both visible */
            <div className={bothRecentVisible ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : ''}>
              {/* Recent Appointments */}
              {(section.id === 'recent-appointments' || (bothRecentVisible && index === firstRecentIndex)) && recentApptsVisible && (
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Recent Appointments
                    </h3>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-4">
                    {appointments.slice(0, 5).map(appointment => (
                      <div 
                        key={appointment.id} 
                        onClick={() => {
                          setSelectedListingForEdit(appointment);
                          setPropertyClients(appointment.client_ids || []);
                          fetchAppointmentMedia(appointment.id);
                          setActiveTab('edit-listing', { listingId: appointment.id });
                        }}
                        className="grid grid-cols-12 gap-2 items-center py-3 border-b last:border-b-0 border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors rounded"
                      >
                        <div className="col-span-5">
                          <p className="text-sm font-semibold text-gray-900 truncate">{appointment.property_address}</p>
                          <p className="text-xs text-gray-500">{appointment.service_type.replace('_', ' ')}</p>
                        </div>
                        <div className="col-span-5">
                          <p className="text-sm font-semibold text-gray-900 truncate">{appointment.client_name}</p>
                          <p className="text-xs text-gray-500">{formatTableDate(appointment.scheduled_date)}</p>
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            appointment.status === 'scheduled' ? 'bg-yellow-100 text-yellow-300' :
                            appointment.status === 'completed' ? 'bg-green-100 text-green-800' :
                            appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-300'
                          }`}>
                            {appointment.status}
                          </span>
                        </div>
                      </div>
                    ))}
                    {appointments.length === 0 && (
                      <p className="text-sm text-center py-6 text-gray-500">No appointments yet</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Recent Clients */}
              {(section.id === 'recent-clients' || (bothRecentVisible && index === firstRecentIndex)) && recentClientsVisible && (
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Recent Clients
                    </h3>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-4">
                    {clients.slice(0, 8).map(client => (
                      <div 
                        key={client.id} 
                        onClick={() => editClient(client)}
                        data-testid={`dashboard-recent-client-${client.id}`}
                        className="grid grid-cols-12 gap-2 items-center py-3 border-b last:border-b-0 border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors rounded"
                      >
                        <div className="col-span-6">
                          <p className="text-sm font-semibold text-gray-900 truncate">{client.name}</p>
                          {client.email && <p className="text-xs text-gray-500 truncate">{client.email}</p>}
                        </div>
                        <div className="col-span-6">
                          {client.company && <p className="text-xs text-gray-500 truncate">{client.company}</p>}
                          {client.phone && <p className="text-xs text-gray-400 truncate">{client.phone}</p>}
                        </div>
                      </div>
                    ))}
                    {clients.length === 0 && (
                      <p className="text-sm text-center py-6 text-gray-500">No clients yet</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {section.id === 'recent-activity' && (
            <RecentActivityFeed
              setActiveTab={setActiveTab}
              appointments={appointments}
              onOpenAppointment={editAppointment}
              onOpenListing={(appt) => {
                setSelectedListingForEdit(appt);
                setActiveTab('edit-listing', { listingId: appt.id });
              }}
            />
          )}

          {section.id === 'stats' && (
            /* Stats Section */
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {[
                { label: 'Total Clients', value: stats.total_clients || 0 },
                { label: 'Total Appointments', value: stats.total_appointments || 0 },
                { label: 'Scheduled', value: stats.scheduled_appointments || 0 },
                { label: 'Completed', value: stats.completed_appointments || 0 },
                { label: 'Media Files', value: stats.total_media_files || 0 },
                { label: 'Galleries', value: stats.total_galleries || 0 }
              ].map((stat, idx) => (
                <div key={idx} className="clara-card p-8 shadow-lg transition-all duration-200 hover:shadow-xl">
                  <div className="text-center">
                    <p className="clara-body-sm mb-3">{stat.label}</p>
                    <p className="clara-heading-2" style={{ margin: 0 }}>{stat.value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          );
        });
      })()}
    </div>
  );
};

const EVENT_COLOR = {
  'appointment.created': 'bg-blue-500',
  'appointment.created.public': 'bg-emerald-500',
  'appointment.confirmed': 'bg-green-500',
  'appointment.cancelled': 'bg-red-500',
  'appointment.rescheduled': 'bg-amber-500',
  'appointment.status_changed': 'bg-gray-500',
  'appointment.products_changed': 'bg-indigo-500',
  'appointment.photographer_changed': 'bg-purple-500',
  'listing.media_delivered': 'bg-cyan-500',
  'listing.media_downloaded': 'bg-sky-500',
  'appointment.payment_received': 'bg-emerald-600',
  'appointment.refunded': 'bg-orange-500',
  'appointment.payment_reminder_sent': 'bg-yellow-500',
};

const EVENT_LABEL = {
  'appointment.created': 'Booked',
  'appointment.created.public': 'Booked online',
  'appointment.confirmed': 'Confirmed',
  'appointment.cancelled': 'Cancelled',
  'appointment.rescheduled': 'Rescheduled',
  'appointment.status_changed': 'Status',
  'appointment.products_changed': 'Products',
  'appointment.photographer_changed': 'Photographer',
  'listing.media_delivered': 'Delivered',
  'listing.media_downloaded': 'Downloaded',
  'appointment.payment_received': 'Payment',
  'appointment.refunded': 'Refunded',
  'appointment.payment_reminder_sent': 'Reminder',
};

const RecentActivityFeed = ({ setActiveTab, appointments, onOpenAppointment, onOpenListing }) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/activity/recent?limit=15`);
        if (!cancelled) {
          setRows(Array.isArray(res.data) ? res.data : []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 403 ? "You need owner or admin access to see activity." : 'Failed to load activity.');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRowClick = (r) => {
    const appt = (appointments || []).find(a => a.id === r.entity_id);
    if (!appt) {
      // Fallback: switch to the relevant tab
      if (setActiveTab) setActiveTab(r.entity_type === 'listing' ? 'media' : 'appointments');
      return;
    }
    if (r.entity_type === 'listing' || r.event_type?.startsWith('listing.')) {
      if (onOpenListing) onOpenListing(appt);
    } else {
      if (onOpenAppointment) onOpenAppointment(appt);
    }
  };

  return (
    <div data-testid="recent-activity-section">
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Recent Activity
        </h3>
      </div>
      <div className="clara-card p-6">
        {loading ? (
          <p className="text-sm text-gray-500">Loading activity…</p>
        ) : error ? (
          <p className="text-sm text-gray-500">{error}</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            title="No activity yet"
            subtitle="As bookings happen and files are delivered, activity will appear here."
          />
        ) : (
          <ul className="space-y-2">
            {rows.map(r => {
              const color = EVENT_COLOR[r.event_type] || 'bg-gray-400';
              const label = EVENT_LABEL[r.event_type] || 'Event';
              const contextLine = r.context_client_name || r.context_address
                ? [r.context_client_name, r.context_address].filter(Boolean).join(' · ')
                : null;
              return (
                <li
                  key={r.id}
                  onClick={() => handleRowClick(r)}
                  className="flex items-start gap-3 py-2 px-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  data-testid={`recent-activity-row-${r.id}`}
                >
                  <span className={`flex-shrink-0 inline-flex items-center justify-center w-2 h-2 mt-2 rounded-full ${color}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex items-center gap-2">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide text-white px-1.5 py-0.5 rounded ${color}`}>{label}</span>
                        <span className="text-sm text-gray-900 truncate">{r.description}</span>
                      </div>
                      <time className="text-xs text-gray-500 whitespace-nowrap" title={moment(r.created_at).format('MMM D, YYYY h:mm:ss A')}>{moment(r.created_at).fromNow()}</time>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      <span className="font-medium text-gray-700">{r.actor_name}</span>
                      {r.actor_role && <span className="text-gray-400"> · {r.actor_role}</span>}
                      {contextLine && <span className="text-gray-400"> · {contextLine}</span>}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
